import { useState } from "react";
import {
  Box,
  Title,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  Loader,
  Center,
  Alert,
  ThemeIcon,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconSearch,
  IconCircleCheck,
  IconMusic,
  IconBan,
  IconRefresh,
  IconLock,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../hooks/usePerformRequest";
import { useAuth } from "../../../hooks/useAuth";

// Only these people may use this screen (must match the server allow-list).
const ACCESS_EMAILS = ["anointedexcel59@gmail.com", "fasanfavour123@gmail.com"];

type Social = {
  platform: string;
  status: string;
  handle?: string | null;
  followers?: number | null;
  posts?: number | null;
};
type UserVerification = {
  userId: string;
  email: string;
  tier: "verified" | "streaming" | null;
  override: string;
  socials: Social[];
};

const OVERRIDES: {
  key: string;
  label: string;
  color: string;
  icon: any;
  desc: string;
}[] = [
  {
    key: "force_verified",
    label: "Give full access (Verified)",
    color: "teal",
    icon: IconCircleCheck,
    desc: "Sees all opportunities — even if the AI rejected their socials.",
  },
  {
    key: "force_streaming",
    label: "Streaming only",
    color: "blue",
    icon: IconMusic,
    desc: "Sees streaming (Spotify-type) opportunities only.",
  },
  {
    key: "banned",
    label: "Block",
    color: "red",
    icon: IconBan,
    desc: "Blocked from earning.",
  },
  {
    key: "none",
    label: "Auto (let the AI decide)",
    color: "gray",
    icon: IconRefresh,
    desc: "Remove the manual preference; go back to automatic verification.",
  },
];

export default function AccessControl() {
  const { admin } = useAuth();
  const allowed = ACCESS_EMAILS.includes(
    String((admin as any)?.email || "").toLowerCase(),
  );

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [user, setUser] = useState<UserVerification | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (!allowed) {
    return (
      <Box p="xl">
        <Center py="xl">
          <Stack align="center" gap="xs">
            <ThemeIcon color="gray" variant="light" size="xl" radius="xl">
              <IconLock size={24} />
            </ThemeIcon>
            <Title order={4}>Restricted</Title>
            <Text c="dimmed" size="sm" ta="center">
              This page is limited to specific people. If you need access, ask
              Anointed.
            </Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  const lookup = async (idOrEmail?: string) => {
    const q = (idOrEmail ?? query).trim();
    if (!q) return;
    setLoading(true);
    setMsg(null);
    if (!idOrEmail) setUser(null);
    try {
      const ep = Endpoints.GetUserVerification(q);
      const res = await PerformRequest<any>({
        method: ep.method,
        route: ep.route,
      });
      const payload = (res.data as any)?.data ?? res.data;
      if (payload?.userId) setUser(payload);
      else setMsg({ ok: false, text: "No member found for that email / ID." });
    } catch {
      setMsg({ ok: false, text: "No member found for that email / ID." });
    } finally {
      setLoading(false);
    }
  };

  const applyOverride = async (override: string, label: string) => {
    if (!user) return;
    setSaving(override);
    setMsg(null);
    try {
      const ep = Endpoints.SetUserOverride(user.userId);
      await PerformRequest<any>({
        method: ep.method,
        route: ep.route,
        data: { override },
      });
      setMsg({ ok: true, text: `Done — "${label}" applied to ${user.email}.` });
      await lookup(user.userId); // refresh state
    } catch {
      setMsg({ ok: false, text: "Couldn't apply that. Try again." });
    } finally {
      setSaving(null);
    }
  };

  const tierBadge = (t: string | null) =>
    t === "verified" ? (
      <Badge color="teal">Verified</Badge>
    ) : t === "streaming" ? (
      <Badge color="blue">Streaming</Badge>
    ) : (
      <Badge color="gray" variant="light">
        Not categorized
      </Badge>
    );

  return (
    <Box p="md">
      <Group gap="xs" mb="xs">
        <IconShieldCheck size={24} />
        <Title order={3}>Verification &amp; Access</Title>
      </Group>
      <Text c="dimmed" size="sm" mb="lg">
        Give a member preference — grant full access even if the AI rejected
        their social accounts. Look them up by email (or member ID), then choose
        their access.
      </Text>

      <Group align="flex-end" mb="lg" wrap="nowrap">
        <TextInput
          label="Member email or ID"
          placeholder="member@email.com"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          style={{ flex: 1 }}
        />
        <Button
          leftSection={<IconSearch size={16} />}
          onClick={() => lookup()}
          loading={loading}
        >
          Look up
        </Button>
      </Group>

      {msg && (
        <Alert
          color={msg.ok ? "teal" : "red"}
          variant="light"
          mb="md"
          withCloseButton
          onClose={() => setMsg(null)}
        >
          {msg.text}
        </Alert>
      )}

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : user ? (
        <Card withBorder radius="md" padding="lg">
          <Group justify="space-between" mb="sm">
            <div>
              <Text fw={700}>{user.email}</Text>
              <Text size="xs" c="dimmed">
                {user.userId}
              </Text>
            </div>
            <Group gap="xs">
              {tierBadge(user.tier)}
              {user.override && user.override !== "none" && (
                <Badge color="grape" variant="filled">
                  Manual: {user.override.replace("force_", "")}
                </Badge>
              )}
            </Group>
          </Group>

          {user.socials?.length > 0 && (
            <Stack gap={4} mb="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Linked socials
              </Text>
              {user.socials.map((s, i) => (
                <Group key={i} gap="xs">
                  <Badge size="sm" variant="light">
                    {s.platform}
                  </Badge>
                  <Text size="sm">{s.handle || "—"}</Text>
                  <Text size="xs" c="dimmed">
                    {s.status}
                    {s.followers != null ? ` · ${s.followers} followers` : ""}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}

          <Text size="sm" fw={600} mb="xs">
            Set their access
          </Text>
          <Stack gap="xs">
            {OVERRIDES.map((o) => {
              const Icon = o.icon;
              const active = user.override === o.key;
              return (
                <Button
                  key={o.key}
                  variant={active ? "filled" : "light"}
                  color={o.color}
                  justify="flex-start"
                  leftSection={<Icon size={16} />}
                  loading={saving === o.key}
                  disabled={active}
                  onClick={() => applyOverride(o.key, o.label)}
                  styles={{ root: { height: "auto", paddingTop: 8, paddingBottom: 8 } }}
                >
                  <div style={{ textAlign: "left" }}>
                    <Text size="sm" fw={600}>
                      {o.label} {active ? "(current)" : ""}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {o.desc}
                    </Text>
                  </div>
                </Button>
              );
            })}
          </Stack>
        </Card>
      ) : null}
    </Box>
  );
}
